// app/components/common/Checkbox/CheckBox.tsx
'use client';

import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Icon } from '../Icon';
import { Typography } from '../Typography/Typography';
import { IconMap } from '../IconMap';
import { cn } from '@workspace/lib';

const checkboxBox = cva(
  'w-[24px] h-[24px] flex items-center justify-center rounded-[5px] border transition-colors peer-focus:outline-2 peer-focus:outline-offset-2 peer-focus:outline-primary',
  {
    variants: {
      tone: {
        default:
          'bg-white border-secondary-300 hover:bg-warmwhite peer-checked:bg-primary peer-checked:border-transparent peer-checked:hover:bg-primary-500 ',
        error: 'bg-error-50 border-error-300 peer-checked:bg-error-50 peer-checked:border-transparent',
      },
      disabled: {
        true: 'bg-secondary-100 border-secondary-300 cursor-not-allowed',
        false: '',
      },
    },
    defaultVariants: { tone: 'default', disabled: false },
  },
);

type BoxVariants = VariantProps<typeof checkboxBox>;

interface CheckBoxProps {
  variant: 'default' | 'error';
  isChecked?: boolean;
  label?: string;
  onChange: (isChecked: boolean) => void;
  isDisabled?: boolean;
  iconName?: keyof typeof IconMap;
  iconSize?: number;
  children?: React.ReactNode;
  className?: string;
  boxClassName?: string;
  labelClassName?: string;
}

export function CheckBox({
  isChecked = false,
  variant,
  onChange,
  isDisabled = false,
  iconName,
  iconSize = 20,
  children,
  className,
  boxClassName,
  labelClassName,
}: CheckBoxProps) {
  const iconColor = isDisabled ? '#4C4C4C' : variant === 'error' ? '#FF0000' : '#fff';
  const tone: NonNullable<BoxVariants['tone']> = variant === 'error' ? 'error' : 'default';

  return (
    <label className={cn('group inline-flex items-center', className)}>
      <input
        type="checkbox"
        checked={isChecked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={isDisabled}
        className="peer sr-only"
      />
      <div className={cn(checkboxBox({ tone, disabled: isDisabled }), boxClassName)}>
        {isChecked && iconName && <Icon name={iconName} size={iconSize} color={iconColor} />}
      </div>
      {children && (
        <Typography variant="bodySm" className={cn('ml-xs', labelClassName)}>
          {children}
        </Typography>
      )}
    </label>
  );
}
